import { Component, OnInit, OnDestroy, ViewContainerRef, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import {
  PairedDevice, MetadataService, BaseComponent, Features, SearchResultsPage, SortDirection, TitleService,
  PairedDevicesNavigationKeys, TranslateNotificationKeys, UserGroup, User, RoleAccessService
} from '../../../core';
import { Router, Params, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import { NgxMatToastService } from 'ngx-mat-toast';
import { TranslateService } from '@ngx-translate/core';
import { scrollElementIntoView } from 'src/app/core/utils/scroll';
import { Sort, SortDirection as MatSortDirection } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ExportFileModalComponent, ExportFileType } from '../../../modals/export-file/export-file-modal/export-file-modal.component';
import { translate } from 'src/app/core/utils/translateServiceHelper';

@Component({
  selector: 'myvirena2-paired-device-search',
  templateUrl: './paired-device-search.component.html',
  styleUrls: ['./paired-device-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PairedDeviceSearchComponent extends BaseComponent implements OnInit, OnDestroy {

  private paramSearchText = 'searchText';
  private paramPageNumber = 'pageNumber';
  private paramPageSize = 'pageSize';
  private paramSortColumn = 'sortColumn';
  private paramSortDirection = 'sortDirection';

  public translationBaseKey = 'PairedDeviceSearch';
  value: string;
  devices: PairedDevice[];
  enableAdd: boolean;
  deviceResults: SearchResultsPage<PairedDevice>;
  sortColumn = 'orgname';
  sortDirection: SortDirection = SortDirection.Ascending;
  sortMatDirection: MatSortDirection;
  userGroup: UserGroup;

  displayColumns = ['orgname', 'facilityname', 'deviceaddress', 'macaddress'];
  pageSize = 20;
  pageNumber = 0;
  sortColumns: Record<string, string> = {
    facilityname: 'Facility.Name',
    orgname: 'Organization.Name',
    macaddress: 'PairedDevice.MacAddress',
    deviceaddress: 'PairedDevice.DeviceAddress'
  };
  ExportFileType = ExportFileType;
  exportButtonsState: string;
  enabledExportButtons: boolean;

  @ViewChildren('matrow', { read: ViewContainerRef }) tableRows: QueryList<ViewContainerRef>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private metadataService: MetadataService,
    private titleService: TitleService,
    private toastr: NgxMatToastService,
    private translate: TranslateService,
    private roleAccessService: RoleAccessService,
    private dialog: MatDialog,
  ) {
    super();
    this.value = '';
    this.exportButtonsState = 'not-allowed';
    this.enabledExportButtons = false;
  }

  doSearch(): void {
    this.pageNumber = 0;
    this.updateData();
  }

  doSort(sortEvent: Sort): void {
    this.sortColumn = sortEvent.active;
    this.sortDirection = sortEvent.direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;
    this.pageNumber = 0;
    this.updateData();
  }

  doPage(pageEvent: PageEvent): void {

    const isPageUp: boolean = pageEvent.pageIndex >= this.pageNumber;

    this.pageSize = pageEvent.pageSize;
    this.pageNumber = pageEvent.pageIndex;
    this.updateData(true, isPageUp);
  }

  scrollToTop(): void {
    scrollElementIntoView(this.tableRows.first.element);
  }

  scrollToBottom(): void {
    scrollElementIntoView(this.tableRows.last.element);
  }

  updateData(doScroll: boolean = false, isPageUp: boolean = true): void {

    const searchParams: Params = {
      searchText: this.value,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      pageSize: this.pageSize,
      pageNumber: this.pageNumber
    };

    // update navigation
    void this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: searchParams,
        replaceUrl: true
      }
    );

    this.subscription.add(
      this.metadataService.searchPairedDevices(this.value,
        this.sortColumns[this.sortColumn.toLowerCase()],
        this.sortDirection,
        this.pageSize,
        +this.pageNumber + 1)
        .subscribe({
          next: (deviceResults: SearchResultsPage<PairedDevice>) => {
            this.deviceResults = deviceResults;

            if (doScroll) {
              if (isPageUp) {
                this.scrollToTop();
              } else {
                this.scrollToBottom();
              }
            }

            this.enabledExportButtons = this.deviceResults &&
              this.roleAccessService.getRole().valueOf() !== UserGroup.TechSupportAdmin.valueOf() &&
              this.roleAccessService.getRole().valueOf() !== UserGroup.SalesAdmin.valueOf();

            if (this.enabledExportButtons) {
              this.exportButtonsState = 'pointer';
            } else {
              this.exportButtonsState = 'not-allowed';
            }
          },
          error: () => {
            this.toastr.error(translate(this.translate, `${TranslateNotificationKeys.Prefix}.${TranslateNotificationKeys.SearchFailure}`));
          }
        }));

  }
  createNew(): void {
    void this.router.navigate([PairedDevicesNavigationKeys.EditFullPath, 0]);
  }

  onRowSelect(device: PairedDevice): void {
    void this.router.navigate([PairedDevicesNavigationKeys.EditFullPath, device.pairedDeviceId]);
  }

  ngOnInit(): void {
    this.subscription.add(this.route.queryParams.pipe(first()).subscribe(params => {

      this.value = params[this.paramSearchText] as string || '';
      this.pageNumber = params[this.paramPageNumber] as number || this.pageNumber;
      this.pageSize = params[this.paramPageSize] as number || this.pageSize;
      this.sortColumn = (params[this.paramSortColumn] as string || this.sortColumn).toLowerCase();
      this.sortDirection = params[this.paramSortDirection] as SortDirection || this.sortDirection;
      this.sortMatDirection = +this.sortDirection === SortDirection.Descending.valueOf() ? 'desc' : 'asc';

      if (this.pageNumber && this.pageSize && this.sortColumn && this.sortDirection) {
        this.updateData();
      }
      this.checkWriteFlag();

      this.subscription.add(this.metadataService.getMe().subscribe((me: User) => {
        this.userGroup = <UserGroup>me.group.displayName;
      }));
    }));

    this.titleService.updateTitleTranslateKey(`${this.translationBaseKey}.Title`);
  }

  checkWriteFlag(): void {
    this.subscription.add(this.metadataService.getFeatures().subscribe((features: Features) => {
      this.enableAdd = features.WriteEnabled;

    }));

  }

  ngOnDestroyInternal(): void {
    // Required by base component
  }

  public get hasCreatePermission(): boolean {
    return this.enableAdd && this.userGroup !== UserGroup.TechSupportAdmin;
  }

  exportFileDialog(fileType: ExportFileType, value: string): void {
    if (!this.enabledExportButtons) {
      return;
    }

    const isTestResultExport = false;
    const modalWidth = 0.27 * window.outerWidth;
    this.dialog.open(ExportFileModalComponent, {
      width: `${modalWidth}px`,
      data: { fileType, value, isTestResultExport }
    });
  }

}
